import { clickOutside } from '@alekstar79/utility'
import { DirectionMode } from '../core/types'

type EventHandlers = {
  onAddStringContainer: () => void;
  onAddTemplateContainer: () => void;
  onAddElementContainer: () => void;
  onAddSnappingContainer: () => void;
  onRestoreContainer: () => void;
  onClearStorage: () => void;
  onClearAll: () => void;
  onToggleGlobalPin: () => void;
  onSnapStepChange: (step: number) => void;
  onSnapModeChange: (mode: DirectionMode) => void;
}

export class DemoUIController
{
  public pinButton: HTMLButtonElement | null = null

  constructor(
    private container: HTMLElement,
    private eventHandlers: EventHandlers
  ) {
    this.createUI()
    this.bindGlobalEvents()
    this.initializeBurgerMenu()
    this.bindStepInputEvents(this.container.querySelector('#snapStep'))
    this.updateSnappingStatus()
  }

  private createUI(): void
  {
    const controls = document.createElement('div')

    controls.className = 'demo-controls'
    controls.innerHTML = `
    <div class="demo-controls-header">
      <button class="demo-controls-toggle" id="demoControlsToggle">
        <i class="fa-solid fa-bars"></i>
      </button>
      <h3 class="demo-controls-title">Container Manager</h3>
    </div>

    <div class="demo-controls-scrollable">
      <div class="demo-controls-content">
        <div class="controls-group">
          <h4>Global Movement Mode</h4>
          <div class="btn-group">
            <button class="btn btn-danger" id="globalPinBtn">
              Enable Pinned Mode
            </button>
          </div>
        </div>

        <div class="controls-group">
          <h4>Snap Settings (for snapping containers)</h4>
          
          <div class="input-group">
            <label for="snapStep">Step Size:</label>
            <input type="number" id="snapStep" value="30" min="1" max="100">
          </div>
          
          <div class="input-group">
            <label for="snapMode">Mode:</label>
            <select id="snapMode">
              <option value="all">All Directions</option>
              <option value="horizontal">Horizontal Only</option>
              <option value="vertical">Vertical Only</option>
            </select>
          </div>
          
          <div class="snapping-status" id="snappingStatus" style="margin-top: 8px; font-size: 0.8rem; color: #6b7280;">
            Current: All Directions, Step: 15px
          </div>
        </div>

        <div class="controls-group">
          <h4>Container Actions</h4>
          
          <div class="btn-group">
            <button class="btn btn-primary" id="addStringContainer">
              String Content
            </button>
            <button class="btn btn-success" id="addTemplateContainer">
              Template Content
            </button>
            <button class="btn btn-warning" id="addElementContainer">
              DOM Element
            </button>
            <button class="btn btn-info" id="addSnappingContainer">
              With Snapping
            </button>
            <button class="btn btn-danger" id="clearAll">
              Clear All
            </button>
          </div>
        </div>

        <div class="controls-group">
          <h4>State Persistence</h4>
          <div class="btn-group">
            <button class="btn btn-success" id="restoreContainer">
              Restore Container
            </button>
            <button class="btn btn-warning" id="clearStorage">
              Clear Storage
            </button>
          </div>
        </div>
      </div>
    </div>`

    const statsPanel = document.createElement('div')
    statsPanel.className = 'stats-panel'
    statsPanel.id = 'statsPanel'

    this.container.appendChild(controls)
    this.container.appendChild(statsPanel)

    this.pinButton = document.getElementById('globalPinBtn') as HTMLButtonElement
  }

  private initializeBurgerMenu(): void
  {
    const toggleButton = document.getElementById('demoControlsToggle') as HTMLButtonElement
    const controls = document.querySelector('.demo-controls') as HTMLElement

    if (!toggleButton || !controls) return

    let isOpen = false

    const toggleMenu = (): void => {
      isOpen = !isOpen

      controls.classList.toggle('demo-controls-open', isOpen)

      const icon = toggleButton.querySelector('i')
      if (icon) {
        icon.classList.toggle('fa-bars', !isOpen)
        icon.classList.toggle('fa-xmark', isOpen)
      }
    }

    toggleButton.addEventListener('click', (e) => {
      e.stopPropagation()
      toggleMenu()
    })
  }

  private bindGlobalEvents(): void
  {
    document.getElementById('globalPinBtn')!.addEventListener('click', this.eventHandlers.onToggleGlobalPin)
    document.getElementById('addStringContainer')!.addEventListener('click', this.eventHandlers.onAddStringContainer)
    document.getElementById('addTemplateContainer')!.addEventListener('click', this.eventHandlers.onAddTemplateContainer)
    document.getElementById('addElementContainer')!.addEventListener('click', this.eventHandlers.onAddElementContainer)
    document.getElementById('addSnappingContainer')!.addEventListener('click', this.eventHandlers.onAddSnappingContainer)
    document.getElementById('restoreContainer')?.addEventListener('click', this.eventHandlers.onRestoreContainer)
    document.getElementById('clearStorage')?.addEventListener('click', this.eventHandlers.onClearStorage)
    document.getElementById('clearAll')?.addEventListener('click', this.eventHandlers.onClearAll)

    document.getElementById('snapStep')
      ?.addEventListener('input', (e) => {
        const step = parseInt((e.target as HTMLInputElement).value)
        this.eventHandlers.onSnapStepChange(step)
        this.updateSnappingStatus()
      })

    document.getElementById('snapMode')
      ?.addEventListener('change', (e) => {
        const mode = (e.target as HTMLSelectElement).value as DirectionMode
        this.eventHandlers.onSnapModeChange(mode)
        this.updateSnappingStatus()
      })
  }

  public updateSnappingStatus(): void
  {
    const snappingStatus = document.getElementById('snappingStatus')
    if (!snappingStatus) return

    const snapStepInput = document.getElementById('snapStep') as HTMLInputElement
    const snapModeSelect = document.getElementById('snapMode') as HTMLSelectElement

    if (snapStepInput && snapModeSelect) {
      const step = snapStepInput.value
      const mode = snapModeSelect.options[snapModeSelect.selectedIndex].text
      snappingStatus.textContent = `Current: ${mode}, Step: ${step}px`
    }
  }

  private bindStepInputEvents(input: HTMLInputElement | null, length: number = 20): void
  {
    if (!input) return
    const allowedValues = this.getAllowedValues(length)

    const handleValueValidation = () => {
      this.validateAndAdjustInputValue(input, allowedValues)
    }

    this.setupInputEventHandlers(input, handleValueValidation, allowedValues)
  }

  private getAllowedValues(length: number): number[]
  {
    return [1, ...Array.from({ length }, (_, i) => (i + 1) * 5)]
  }

  private validateAndAdjustInputValue(input: HTMLInputElement, allowedValues: number[]): void
  {
    (document.activeElement as HTMLElement).blur()

    const currentValue = Number(input.value)
    if (!allowedValues.includes(currentValue)) {
      input.value = this.findClosestValue(currentValue, allowedValues).toString()
    }
  }

  private findClosestValue(currentValue: number, allowedValues: number[]): number
  {
    return allowedValues.reduce((prev, curr) =>
      Math.abs(curr - currentValue) < Math.abs(prev - currentValue) ? curr : prev
    )
  }

  private setupInputEventHandlers(
    input: HTMLInputElement,
    validationHandler: () => void,
    allowedValues: number[]
  ): void {
    input.addEventListener('keydown', (event) => {
      this.handleStepInputKeyDown(event, input, allowedValues)
    })

    input.addEventListener('blur', validationHandler)
    input.addEventListener('change', () => input.focus())
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') validationHandler()
    })

    clickOutside(input, validationHandler)
  }

  private handleStepInputKeyDown(
    event: KeyboardEvent,
    input: HTMLInputElement,
    allowedValues: number[]
  ): void {
    if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return
    event.preventDefault()
    this.adjustStepValue(event.key, input, allowedValues)
  }

  private adjustStepValue(
    direction: string,
    input: HTMLInputElement,
    allowedValues: number[]
  ): void {
    let currentIndex = allowedValues.indexOf(Number(input.value))

    if (currentIndex === -1) {
      currentIndex = allowedValues.findIndex(v => v > Number(input.value))

      if (currentIndex === -1) {
        currentIndex = allowedValues.length - 1;
      }
    }

    if (direction === 'ArrowUp' && currentIndex < allowedValues.length - 1) {
      input.value = `${allowedValues[currentIndex + 1]}`
    } else if (direction === 'ArrowDown' && currentIndex > 0) {
      input.value = `${allowedValues[currentIndex - 1]}`
    }
  }

  public updateRestoreButton(count: number): void
  {
    const restoreButton = document.getElementById('restoreContainer') as HTMLButtonElement

    if (restoreButton) {
      restoreButton.disabled = count === 0
      restoreButton.classList.toggle('btn-disabled', count === 0)
      restoreButton.title = count === 0
        ? 'No closed containers to restore'
        : `Restore last closed container (${count} available)`
    }
  }
}
